<template>

  <b-modal

    id="project-form-modal"

    :visible="value"

    :title="modalTitle"

    :size="isEditMode ? 'lg' : 'lg'"

    scrollable

    cancel-variant="outline-secondary"

    @change="$emit('input', $event)"

    @hidden="onHidden"

  >

    <!-- Step indicator (create mode only) -->

    <div v-if="!isEditMode" class="project-wizard-steps d-flex align-items-center mb-2">

      <div class="project-wizard-step" :class="{ active: step === 1, done: step > 1 }">

        <span class="project-wizard-step-num">1</span>

        <span class="project-wizard-step-label">Project details</span>

      </div>

      <div class="project-wizard-divider" />

      <div class="project-wizard-step" :class="{ active: step === 2, done: step > 2 }">

        <span class="project-wizard-step-num">2</span>

        <span class="project-wizard-step-label">Team</span>

      </div>

      <div class="project-wizard-divider" />

      <div class="project-wizard-step" :class="{ active: step === 3 }">

        <span class="project-wizard-step-num">3</span>

        <span class="project-wizard-step-label">Responsibilities</span>

      </div>

    </div>



    <!-- Edit mode: tabbed settings -->

    <b-tabs

      v-if="isEditMode"

      v-model="editTab"

      class="project-settings-tabs mb-1"

      content-class="mt-0"

    >

      <b-tab title="Details" />

      <b-tab v-if="canManage" title="Team" />

      <b-tab v-if="canManage" title="Responsibilities" />

    </b-tabs>



    <!-- Step 1 / edit details tab -->

    <div v-show="detailsVisible">

      <validation-observer ref="projectFormRules">

        <b-form @submit.prevent>

          <b-row>

            <b-col cols="12" md="8">

              <b-form-group label="Project Name *" label-for="project-name">

                <validation-provider #default="{ errors }" name="Name" rules="required">

                  <b-form-input

                    id="project-name"

                    v-model="form.name"

                    placeholder="e.g. Q3 Client Onboarding"

                    :state="errors.length > 0 ? false : null"

                  />

                  <small class="text-danger">{{ errors[0] }}</small>

                </validation-provider>

              </b-form-group>

            </b-col>

            <b-col cols="12" md="4">

              <b-form-group label="Status" label-for="project-status">

                <b-form-select

                  id="project-status"

                  v-model="form.status"

                  :options="statusOptions"

                  value-field="key"

                  text-field="label"

                />

              </b-form-group>

            </b-col>

            <b-col cols="12">

              <b-form-group label="Description" label-for="project-description">

                <b-form-textarea

                  id="project-description"

                  v-model="form.description"

                  rows="2"

                  placeholder="Brief description of goals and scope..."

                />

              </b-form-group>

            </b-col>

            <b-col cols="12">

              <b-form-group label="Color">

                <div class="d-flex flex-wrap" style="gap: 8px;">

                  <button

                    v-for="color in colorOptions"

                    :key="color"

                    type="button"

                    class="project-color-swatch"

                    :class="{ active: form.color === color }"

                    :style="{ backgroundColor: color }"

                    :aria-label="`Color ${color}`"

                    @click="form.color = color"

                  />

                </div>

              </b-form-group>

            </b-col>

          </b-row>

        </b-form>

      </validation-observer>

    </div>



    <!-- Edit mode: Team tab -->

    <div v-if="editTeamVisible" class="project-team-wizard">

      <p class="text-muted small mb-1">

        Update roles or remove members. The project creator cannot be removed.

      </p>



      <h6 class="small font-weight-bold mb-50">

        <feather-icon icon="UsersIcon" size="14" class="mr-50" />

        Roster

        <b-badge variant="light-secondary" pill class="ml-50">{{ visibleEditMembers.length + pendingMembers.length }}</b-badge>

      </h6>



      <ul

        v-if="visibleEditMembers.length || pendingMembers.length"

        class="list-unstyled mb-1 project-pending-members"

      >

        <li

          v-for="member in visibleEditMembers"

          :key="`existing-${member.empId}`"

          class="project-pending-member d-flex align-items-start"

        >

          <b-avatar

            size="28"

            variant="light-primary"

            :text="avatarText(member.name)"

            class="flex-shrink-0 mt-25"

          />

          <div class="flex-grow-1 ml-75 min-width-0">

            <div class="font-weight-bold small">{{ member.name }}</div>

            <b-form-select

              v-model="member.role"

              :options="teamMemberRoleOptions(member.empId)"

              value-field="key"

              text-field="label"

              size="sm"

              class="mt-25"

              style="max-width: 180px;"

            />

          </div>

          <b-button

            v-if="!isProjectCreator(member.empId)"

            size="sm"

            variant="outline-danger"

            class="px-50 py-0 flex-shrink-0"

            @click="markMemberRemoved(member.empId)"

          >

            <feather-icon icon="XIcon" size="12" />

          </b-button>

        </li>



        <li

          v-for="(member, idx) in pendingMembers"

          :key="`pending-${member.empId}`"

          class="project-pending-member d-flex align-items-start"

        >

          <b-avatar

            size="28"

            variant="light-primary"

            :text="avatarText(member.name)"

            class="flex-shrink-0 mt-25"

          />

          <div class="flex-grow-1 ml-75 min-width-0">

            <div class="font-weight-bold small">{{ member.name }}</div>

            <project-role-badge :role="member.role" :short="true" />

            <b-badge variant="light-info" pill class="ml-25 small">New</b-badge>

          </div>

          <b-button

            size="sm"

            variant="outline-danger"

            class="px-50 py-0 flex-shrink-0"

            @click="removePendingMember(idx)"

          >

            <feather-icon icon="XIcon" size="12" />

          </b-button>

        </li>

      </ul>

      <b-alert v-else show variant="light" class="py-75 px-1 mb-1 small text-muted">

        <feather-icon icon="UsersIcon" size="14" class="mr-50" />

        No members on the roster yet.

      </b-alert>



      <b-card no-body class="p-1 border project-member-form-card mb-1">

        <h6 class="small font-weight-bold mb-25">

          <feather-icon icon="UserPlusIcon" size="14" class="mr-50" />

          Add members

        </h6>



        <b-form-group label="Employees" label-size="sm" class="mb-50">

          <b-dropdown

            id="edit-new-members"

            variant="link"

            toggle-class="form-control form-control-sm assignee-multiselect-toggle d-flex align-items-center justify-content-between"

            block

            no-caret

            boundary="window"

            :popper-opts="{ positionFixed: true }"

            :disabled="!availableUserOptions.length"

          >

            <template #button-content>

              <span class="text-truncate">{{ newMemberEmpIdsSummary }}</span>

              <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

            </template>

            <div class="assignee-multiselect-menu" @click.stop>

              <p v-if="!availableUserOptions.length" class="text-muted small px-1 mb-0">

                Everyone available is already on the team.

              </p>

              <b-form-checkbox

                v-for="option in availableUserOptions"

                :key="option.value"

                v-model="newMemberEmpIds"

                :value="option.value"

                class="assignee-multiselect-item"

              >

                {{ option.label }}

              </b-form-checkbox>

            </div>

          </b-dropdown>

          <div v-if="newMemberEmpIds.length" class="d-flex flex-wrap mt-50 project-selected-chips">

            <b-badge

              v-for="empId in newMemberEmpIds"

              :key="empId"

              variant="light-primary"

              pill

              class="mr-25 mb-25"

            >

              {{ getAssigneeLabel(empId, availableUserOptions) }}

            </b-badge>

          </div>

        </b-form-group>



        <b-form-group label="Role" label-size="sm" class="mb-50">

          <b-form-select

            v-model="newMemberRole"

            :options="addMemberRoleOptions"

            value-field="key"

            text-field="label"

            size="sm"

          />

        </b-form-group>



        <b-button

          size="sm"

          variant="outline-primary"

          :disabled="!newMemberEmpIds.length"

          @click="addPendingMember"

        >

          <feather-icon icon="PlusIcon" size="13" class="mr-25" />

          Add {{ newMemberEmpIds.length > 1 ? `${newMemberEmpIds.length} members` : 'member' }}

        </b-button>

      </b-card>

    </div>



    <!-- Edit mode: Responsibilities tab -->

    <div v-if="editResponsibilitiesVisible" class="project-team-wizard">

      <p class="text-muted small mb-1">

        Assign work to teammates and attach reference documents as needed.

      </p>



      <h6 class="small font-weight-bold mb-50">

        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />

        On this project

        <b-badge variant="light-secondary" pill class="ml-50">{{ existingResponsibilitiesGrouped.length }}</b-badge>

      </h6>



      <ul v-if="existingResponsibilitiesGrouped.length" class="list-unstyled mb-1 project-pending-responsibilities">

        <li

          v-for="group in existingResponsibilitiesGrouped"

          :key="group.groupKey"

          class="project-pending-responsibility d-flex align-items-start"

        >

          <feather-icon icon="CheckSquareIcon" size="14" class="text-muted flex-shrink-0 mt-25 mr-50" />

          <div class="flex-grow-1 min-width-0">

            <div class="font-weight-bold small text-truncate">{{ group.responsibilityTitle }}</div>

            <p v-if="group.responsibilityDescription" class="text-muted small mb-0 text-truncate">{{ group.responsibilityDescription }}</p>

            <div class="d-flex flex-wrap mt-25 project-selected-chips">

              <b-badge

                v-for="entry in group.completionEntries"

                :key="`${group.groupKey}-${entry.empId}`"

                variant="light-secondary"

                pill

                class="mr-25 mb-25 small"

              >

                {{ entry.name }}

              </b-badge>

            </div>

            <small v-if="group.attachments && group.attachments.length" class="text-muted d-block mt-25">

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              {{ group.attachments.length }} {{ group.attachments.length === 1 ? 'document' : 'documents' }}

            </small>

          </div>

        </li>

      </ul>

      <b-alert v-else show variant="light" class="py-75 px-1 mb-1 small text-muted">

        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />

        None yet.

      </b-alert>



      <h6 class="small font-weight-bold mb-50 mt-1">

        <feather-icon icon="PlusSquareIcon" size="14" class="mr-50" />

        Pending additions

        <b-badge v-if="pendingResponsibilities.length" variant="light-primary" pill class="ml-50">{{ pendingResponsibilities.length }}</b-badge>

      </h6>



      <ul v-if="pendingResponsibilities.length" class="list-unstyled mb-1 project-pending-responsibilities">

        <li

          v-for="(resp, idx) in pendingResponsibilities"

          :key="`edit-pending-${resp.responsibilityTitle}-${idx}`"

          class="project-pending-responsibility d-flex align-items-start"

        >

          <feather-icon icon="CheckSquareIcon" size="14" class="text-primary flex-shrink-0 mt-25 mr-50" />

          <div class="flex-grow-1 min-width-0">

            <div class="font-weight-bold small text-truncate">{{ resp.responsibilityTitle }}</div>

            <p v-if="resp.responsibilityDescription" class="text-muted small mb-0 text-truncate">{{ resp.responsibilityDescription }}</p>

            <div class="d-flex flex-wrap mt-25 project-selected-chips">

              <b-badge

                v-for="empId in resp.targetEmpIds"

                :key="`${resp.responsibilityTitle}-${empId}`"

                variant="light-secondary"

                pill

                class="mr-25 mb-25 small"

              >

                {{ getAssigneeLabel(empId, responsibilityAssigneeOptions) }}

              </b-badge>

            </div>

            <small v-if="resp.attachments && resp.attachments.length" class="text-muted d-block mt-25">

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              {{ resp.attachments.length }} {{ resp.attachments.length === 1 ? 'document' : 'documents' }}

            </small>

          </div>

          <b-button

            size="sm"

            variant="outline-danger"

            class="px-50 py-0 flex-shrink-0"

            @click="removePendingResponsibility(idx)"

          >

            <feather-icon icon="XIcon" size="12" />

          </b-button>

        </li>

      </ul>



      <b-card no-body class="p-1 border project-member-form-card">

        <h6 class="small font-weight-bold mb-25">

          <feather-icon icon="PlusSquareIcon" size="14" class="mr-50" />

          New responsibility

        </h6>



        <b-form-group label="Title *" label-size="sm" class="mb-50">

          <b-form-input

            v-model="newResponsibilityTitle"

            size="sm"

            placeholder="e.g. Payroll module review"

          />

        </b-form-group>



        <b-form-group label="Description" label-size="sm" class="mb-50">

          <b-form-textarea

            v-model="newResponsibilityDescription"

            rows="2"

            size="sm"

            placeholder="What needs to be done... (optional)"

          />

        </b-form-group>



        <b-form-group label="Assign to *" label-size="sm" class="mb-50">

          <b-dropdown

            id="edit-responsibility-assignees"

            variant="link"

            toggle-class="form-control form-control-sm assignee-multiselect-toggle d-flex align-items-center justify-content-between"

            block

            no-caret

            boundary="window"

            :popper-opts="{ positionFixed: true }"

            :disabled="!responsibilityAssigneeOptions.length"

          >

            <template #button-content>

              <span class="text-truncate">{{ responsibilityAssigneesSummary }}</span>

              <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

            </template>

            <div class="assignee-multiselect-menu" @click.stop>

              <p v-if="!responsibilityAssigneeOptions.length" class="text-muted small px-1 mb-0">

                Add team members on the Team tab before assigning responsibilities.

              </p>

              <b-form-checkbox

                v-for="option in responsibilityAssigneeOptions"

                :key="option.empId"

                v-model="newResponsibilityAssignees"

                :value="option.empId"

                class="assignee-multiselect-item"

              >

                {{ option.label }}

              </b-form-checkbox>

            </div>

          </b-dropdown>

          <div v-if="newResponsibilityAssignees.length" class="d-flex flex-wrap mt-50 project-selected-chips">

            <b-badge

              v-for="empId in newResponsibilityAssignees"

              :key="empId"

              variant="light-primary"

              pill

              class="mr-25 mb-25"

            >

              {{ getAssigneeLabel(empId, responsibilityAssigneeOptions) }}

            </b-badge>

          </div>

        </b-form-group>



        <div v-if="showResponsibilityAttachments" class="mb-50">

          <b-form-group label-size="sm" class="mb-0">

            <template #label>

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              Reference documents

              <small class="text-muted font-weight-normal">(optional)</small>

            </template>

            <ul v-if="newResponsibilityAttachments.length" class="list-unstyled mb-50">

              <li

                v-for="(att, idx) in newResponsibilityAttachments"

                :key="att.id"

                class="project-member-attachment-row d-flex align-items-center"

              >

                <feather-icon icon="FileTextIcon" size="13" class="text-muted mr-50 flex-shrink-0" />

                <span class="text-truncate flex-grow-1 small">{{ att.name }}</span>

                <small v-if="att.size" class="text-muted mr-50 flex-shrink-0">{{ formatSize(att.size) }}</small>

                <b-button

                  size="sm"

                  variant="outline-danger"

                  class="px-50 py-0"

                  @click="removeNewResponsibilityAttachment(idx)"

                >

                  <feather-icon icon="XIcon" size="11" />

                </b-button>

              </li>

            </ul>

            <b-form-file

              id="edit-responsibility-attachments"

              v-model="newResponsibilityFiles"

              :accept="attachmentAccept"

              multiple

              size="sm"

              placeholder="Choose files..."

              browse-text="Attach"

              @input="onNewResponsibilityFilesSelected"

            />

          </b-form-group>

        </div>

        <b-button

          v-else

          variant="link"

          class="p-0 add-responsibility-attach-toggle mb-50"

          @click="showResponsibilityAttachments = true"

        >

          <feather-icon icon="PaperclipIcon" size="13" class="mr-25" />

          Attach reference documents

        </b-button>

        <b-alert v-if="responsibilityFileErrors.length" show variant="danger" class="p-50 mb-50 small">

          <div v-for="(err, i) in responsibilityFileErrors" :key="i">{{ err }}</div>

        </b-alert>



        <b-button

          size="sm"

          variant="outline-primary"

          :disabled="!canSubmitPendingResponsibility || processingFiles"

          @click="addPendingResponsibility"

        >

          <feather-icon icon="PlusIcon" size="13" class="mr-25" />

          Add responsibility

        </b-button>

      </b-card>

    </div>



    <!-- Step 2: Team selection (create mode) -->

    <div v-if="!isEditMode && step === 2" class="project-team-wizard">

      <p class="text-muted small mb-1">

        You're added as Project Manager. Teammates are optional — skip to add them later.

      </p>



      <ul v-if="pendingMembers.length" class="list-unstyled mb-1 project-pending-members">

        <li

          v-for="(member, idx) in pendingMembers"

          :key="member.empId"

          class="project-pending-member d-flex align-items-start"

        >

          <b-avatar

            size="28"

            variant="light-primary"

            :text="avatarText(member.name)"

            class="flex-shrink-0 mt-25"

          />

          <div class="flex-grow-1 ml-75 min-width-0">

            <div class="font-weight-bold small">{{ member.name }}</div>

            <project-role-badge :role="member.role" :short="true" />

          </div>

          <b-button

            size="sm"

            variant="outline-danger"

            class="px-50 py-0 flex-shrink-0"

            @click="removePendingMember(idx)"

          >

            <feather-icon icon="XIcon" size="12" />

          </b-button>

        </li>

      </ul>

      <b-alert v-else show variant="light" class="py-75 px-1 mb-1 small text-muted">

        <feather-icon icon="UsersIcon" size="14" class="mr-50" />

        No teammates added yet.

      </b-alert>



      <b-card no-body class="p-1 border project-member-form-card mb-1">

        <h6 class="small font-weight-bold mb-25">

          <feather-icon icon="UserPlusIcon" size="14" class="mr-50" />

          Add members

        </h6>

        <p class="text-muted small mb-75">

          Select employees and a role — all selected will share that role.

        </p>



        <b-form-group label="Employees" label-size="sm" class="mb-50">

          <b-dropdown

            id="wizard-new-members"

            variant="link"

            toggle-class="form-control form-control-sm assignee-multiselect-toggle d-flex align-items-center justify-content-between"

            block

            no-caret

            boundary="window"

            :popper-opts="{ positionFixed: true }"

            :disabled="!availableUserOptions.length"

          >

            <template #button-content>

              <span class="text-truncate">{{ newMemberEmpIdsSummary }}</span>

              <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

            </template>

            <div class="assignee-multiselect-menu" @click.stop>

              <p v-if="!availableUserOptions.length" class="text-muted small px-1 mb-0">

                Everyone available is already on the team.

              </p>

              <b-form-checkbox

                v-for="option in availableUserOptions"

                :key="option.value"

                v-model="newMemberEmpIds"

                :value="option.value"

                class="assignee-multiselect-item"

              >

                {{ option.label }}

              </b-form-checkbox>

            </div>

          </b-dropdown>

          <div v-if="newMemberEmpIds.length" class="d-flex flex-wrap mt-50 project-selected-chips">

            <b-badge

              v-for="empId in newMemberEmpIds"

              :key="empId"

              variant="light-primary"

              pill

              class="mr-25 mb-25"

            >

              {{ getAssigneeLabel(empId, availableUserOptions) }}

            </b-badge>

          </div>

        </b-form-group>



        <b-form-group label="Role" label-size="sm" class="mb-50">

          <b-form-select

            v-model="newMemberRole"

            :options="addMemberRoleOptions"

            value-field="key"

            text-field="label"

            size="sm"

          />

        </b-form-group>



        <b-button

          size="sm"

          variant="outline-primary"

          :disabled="!newMemberEmpIds.length"

          @click="addPendingMember"

        >

          <feather-icon icon="PlusIcon" size="13" class="mr-25" />

          Add {{ newMemberEmpIds.length > 1 ? `${newMemberEmpIds.length} members` : 'member' }}

        </b-button>

      </b-card>

    </div>



    <!-- Step 3: Responsibilities (create mode) -->

    <div v-if="!isEditMode && step === 3" class="project-team-wizard">

      <p class="text-muted small mb-1">

        Assign work to teammates and attach reference documents as needed. Skip to add later.

      </p>



      <ul v-if="pendingResponsibilities.length" class="list-unstyled mb-1 project-pending-responsibilities">

        <li

          v-for="(resp, idx) in pendingResponsibilities"

          :key="`${resp.responsibilityTitle}-${idx}`"

          class="project-pending-responsibility d-flex align-items-start"

        >

          <feather-icon icon="CheckSquareIcon" size="14" class="text-primary flex-shrink-0 mt-25 mr-50" />

          <div class="flex-grow-1 min-width-0">

            <div class="font-weight-bold small text-truncate">{{ resp.responsibilityTitle }}</div>

            <p v-if="resp.responsibilityDescription" class="text-muted small mb-0 text-truncate">{{ resp.responsibilityDescription }}</p>

            <div class="d-flex flex-wrap mt-25 project-selected-chips">

              <b-badge

                v-for="empId in resp.targetEmpIds"

                :key="`${resp.responsibilityTitle}-${empId}`"

                variant="light-secondary"

                pill

                class="mr-25 mb-25 small"

              >

                {{ getAssigneeLabel(empId, responsibilityAssigneeOptions) }}

              </b-badge>

            </div>

            <small v-if="resp.attachments && resp.attachments.length" class="text-muted d-block mt-25">

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              {{ resp.attachments.length }} {{ resp.attachments.length === 1 ? 'document' : 'documents' }}

            </small>

          </div>

          <b-button

            size="sm"

            variant="outline-danger"

            class="px-50 py-0 flex-shrink-0"

            @click="removePendingResponsibility(idx)"

          >

            <feather-icon icon="XIcon" size="12" />

          </b-button>

        </li>

      </ul>

      <b-alert v-else show variant="light" class="py-75 px-1 mb-1 small text-muted">

        <feather-icon icon="CheckSquareIcon" size="14" class="mr-50" />

        None added yet.

      </b-alert>



      <b-card no-body class="p-1 border project-member-form-card">

        <h6 class="small font-weight-bold mb-25">

          <feather-icon icon="PlusSquareIcon" size="14" class="mr-50" />

          New responsibility

        </h6>



        <b-form-group label="Title *" label-size="sm" class="mb-50">

          <b-form-input

            v-model="newResponsibilityTitle"

            size="sm"

            placeholder="e.g. Payroll module review"

          />

        </b-form-group>



        <b-form-group label="Description" label-size="sm" class="mb-50">

          <b-form-textarea

            v-model="newResponsibilityDescription"

            rows="2"

            size="sm"

            placeholder="What needs to be done... (optional)"

          />

        </b-form-group>



        <b-form-group label="Assign to *" label-size="sm" class="mb-50">

          <b-dropdown

            id="wizard-responsibility-assignees"

            variant="link"

            toggle-class="form-control form-control-sm assignee-multiselect-toggle d-flex align-items-center justify-content-between"

            block

            no-caret

            boundary="window"

            :popper-opts="{ positionFixed: true }"

            :disabled="!responsibilityAssigneeOptions.length"

          >

            <template #button-content>

              <span class="text-truncate">{{ responsibilityAssigneesSummary }}</span>

              <feather-icon icon="ChevronDownIcon" size="14" class="ml-50 flex-shrink-0" />

            </template>

            <div class="assignee-multiselect-menu" @click.stop>

              <b-form-checkbox

                v-for="option in responsibilityAssigneeOptions"

                :key="option.empId"

                v-model="newResponsibilityAssignees"

                :value="option.empId"

                class="assignee-multiselect-item"

              >

                {{ option.label }}

              </b-form-checkbox>

            </div>

          </b-dropdown>

          <div v-if="newResponsibilityAssignees.length" class="d-flex flex-wrap mt-50 project-selected-chips">

            <b-badge

              v-for="empId in newResponsibilityAssignees"

              :key="empId"

              variant="light-primary"

              pill

              class="mr-25 mb-25"

            >

              {{ getAssigneeLabel(empId, responsibilityAssigneeOptions) }}

            </b-badge>

          </div>

        </b-form-group>



        <div v-if="showResponsibilityAttachments || newResponsibilityAttachments.length" class="mb-50">

          <b-form-group label-for="wizard-responsibility-attachments" class="mb-0">

            <template #label>

              <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />

              Reference documents

              <small class="text-muted font-weight-normal">(optional)</small>

            </template>

            <ul v-if="newResponsibilityAttachments.length" class="list-unstyled mb-50">

              <li

                v-for="(att, idx) in newResponsibilityAttachments"

                :key="att.id"

                class="project-member-attachment-row d-flex align-items-center"

              >

                <feather-icon icon="FileTextIcon" size="13" class="text-muted mr-50 flex-shrink-0" />

                <span class="text-truncate flex-grow-1 small">{{ att.name }}</span>

                <small v-if="att.size" class="text-muted mr-50 flex-shrink-0">{{ formatSize(att.size) }}</small>

                <b-button

                  size="sm"

                  variant="outline-danger"

                  class="px-50 py-0"

                  @click="removeNewResponsibilityAttachment(idx)"

                >

                  <feather-icon icon="XIcon" size="11" />

                </b-button>

              </li>

            </ul>

            <b-form-file

              id="wizard-responsibility-attachments"

              v-model="newResponsibilityFiles"

              :accept="attachmentAccept"

              multiple

              size="sm"

              placeholder="Choose files..."

              browse-text="Attach"

              @input="onNewResponsibilityFilesSelected"

            />

          </b-form-group>

        </div>

        <b-button

          v-else

          variant="link"

          class="p-0 add-responsibility-attach-toggle mb-50"

          @click="showResponsibilityAttachments = true"

        >

          <feather-icon icon="PaperclipIcon" size="13" class="mr-25" />

          Attach reference documents

        </b-button>

        <b-alert v-if="responsibilityFileErrors.length" show variant="danger" class="p-50 mb-50 small">

          <div v-for="(err, i) in responsibilityFileErrors" :key="i">{{ err }}</div>

        </b-alert>



        <b-button

          size="sm"

          variant="outline-primary"

          :disabled="!canSubmitPendingResponsibility || processingFiles"

          @click="addPendingResponsibility"

        >

          <feather-icon icon="PlusIcon" size="13" class="mr-25" />

          Add responsibility

        </b-button>

      </b-card>

    </div>



    <template #modal-footer>

      <div class="w-100 d-flex justify-content-between align-items-center">

        <b-button

          v-if="!isEditMode && step > 1"

          variant="outline-secondary"

          :disabled="submitting"

          @click="goBack"

        >

          Back

        </b-button>

        <span v-else />



        <div class="d-flex" style="gap: 8px;">

          <b-button

            variant="outline-secondary"

            :disabled="submitting"

            @click="$emit('input', false)"

          >

            Cancel

          </b-button>



          <template v-if="isEditMode">

            <b-button

              variant="primary"

              :disabled="submitting || processingFiles"

              @click="handleEditSave"

            >

              Save

            </b-button>

          </template>



          <template v-else-if="step === 1">

            <b-button

              variant="primary"

              :disabled="submitting"

              @click="goToStep2"

            >

              Next

            </b-button>

          </template>



          <template v-else-if="step === 2">

            <b-button

              variant="outline-secondary"

              :disabled="submitting"

              @click="handleCreateFinish({ skipTeam: true, skipResponsibilities: true })"

            >

              Skip

            </b-button>

            <b-button

              variant="primary"

              :disabled="submitting"

              @click="goToStep3"

            >

              Next

            </b-button>

          </template>



          <template v-else>

            <b-button

              variant="outline-secondary"

              :disabled="submitting || processingFiles"

              @click="handleCreateFinish({ skipResponsibilities: true })"

            >

              Skip

            </b-button>

            <b-button

              variant="primary"

              :disabled="submitting || processingFiles"

              @click="handleCreateFinish({})"

            >

              Create project

            </b-button>

          </template>

        </div>

      </div>

    </template>

  </b-modal>

</template>



<script>

import {

  BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormFile,

  BRow, BCol, BButton, BCard, BAvatar, BAlert, BBadge, BDropdown, BFormCheckbox, BTabs, BTab,

} from 'bootstrap-vue'

import { ValidationObserver, ValidationProvider } from 'vee-validate'

import { required } from '@validations'

import { avatarText } from '@core/utils/filter'

import { getEmpId } from '@/utils/task-permissions'

import { canManageProject } from '@/utils/project-permissions'

import ProjectRoleBadge from './ProjectRoleBadge.vue'

import { buildResponsibilityStepsGrouped } from '@/utils/project-responsibility-tasks'

import {

  PROJECT_STATUSES,

  PROJECT_STATUS_OPTIONS,

  PROJECT_COLOR_OPTIONS,

  PROJECT_ROLE_OPTIONS,

  PROJECT_ROLES,

  PROJECT_MEMBER_ATTACHMENT_ACCEPT,

  projectId,

  normalizeMembers,

  buildResponsibilitySteps,

  isValidResponsibilityNote,

  formatMemberFileSize,

  filesToMemberAttachments,

} from '@/utils/project-config'



function blankForm() {

  return {

    id: null,

    name: '',

    description: '',

    status: PROJECT_STATUSES.ACTIVE,

    color: PROJECT_COLOR_OPTIONS[0],

  }

}



export default {

  name: 'ProjectFormModal',

  components: {

    BModal, BForm, BFormGroup, BFormInput, BFormSelect, BFormTextarea, BFormFile,

    BRow, BCol, BButton, BCard, BAvatar, BAlert, BBadge, BDropdown, BFormCheckbox, BTabs, BTab,

    ValidationObserver, ValidationProvider,

    ProjectRoleBadge,

  },

  model: { prop: 'value', event: 'input' },

  props: {

    value: { type: Boolean, default: false },

    project: { type: Object, default: null },

    currentUser: { type: Object, default: () => ({}) },

    assignableUsers: { type: Array, default: () => [] },

    submitting: { type: Boolean, default: false },

  },

  data() {

    return {

      required,

      form: blankForm(),

      step: 1,

      editTab: 0,

      editMembers: [],

      removedMemberEmpIds: [],

      originalMemberRoles: {},

      pendingMembers: [],

      pendingResponsibilities: [],

      newMemberEmpIds: [],

      newMemberRole: PROJECT_ROLES.CONTRIBUTOR,

      newResponsibilityTitle: '',

      newResponsibilityDescription: '',

      newResponsibilityAssignees: [],

      newResponsibilityAttachments: [],

      newResponsibilityFiles: null,

      responsibilityFileErrors: [],

      showResponsibilityAttachments: false,

      processingFiles: false,

      attachmentAccept: PROJECT_MEMBER_ATTACHMENT_ACCEPT,

      statusOptions: PROJECT_STATUS_OPTIONS,

      colorOptions: PROJECT_COLOR_OPTIONS,

      roleOptions: PROJECT_ROLE_OPTIONS,

    }

  },

  computed: {

    isEditMode() {

      return !!(this.project && projectId(this.project))

    },

    canManage() {

      return canManageProject(this.currentUser, this.project)

    },

    detailsVisible() {

      if (this.isEditMode) return this.editTab === 0

      return this.step === 1

    },

    editTeamVisible() {

      return this.isEditMode && this.editTab === 1 && this.canManage

    },

    editResponsibilitiesVisible() {

      return this.isEditMode && this.editTab === 2 && this.canManage

    },

    visibleEditMembers() {

      const removed = new Set(this.removedMemberEmpIds.map(String))

      return this.editMembers.filter(m => !removed.has(String(m.empId)))

    },

    existingResponsibilitiesGrouped() {

      if (!this.project) return []

      return buildResponsibilityStepsGrouped(buildResponsibilitySteps(this.project))

    },

    modalTitle() {

      if (this.isEditMode) return 'Project settings'

      if (this.step === 1) return 'New Project'

      if (this.step === 2) return 'Team'

      return 'Responsibilities'

    },

    currentEmpId() {

      return getEmpId(this.currentUser)

    },

    creatorDisplayName() {

      const u = this.currentUser || {}

      return `${u.first_name || ''} ${u.last_name || ''}`.trim()

        || u.title

        || 'You'

    },

    availableUsers() {

      const excluded = new Set(this.pendingMembers.map(m => String(m.empId)))

      if (this.isEditMode) {

        for (const m of this.editMembers) {

          if (!this.removedMemberEmpIds.includes(String(m.empId))) {

            excluded.add(String(m.empId))

          }

        }

      } else if (this.currentEmpId != null) {

        excluded.add(String(this.currentEmpId))

      }

      return (this.assignableUsers || [])

        .filter(u => {

          const id = String(u.emp_id ?? u.empId ?? u.id)

          return !excluded.has(id)

        })

        .map(u => ({

          ...u,

          emp_id: u.emp_id ?? u.empId ?? u.id,

          label: u.first_name

            ? `${u.first_name} ${u.last_name || ''}`.trim()

            : (u.name || u.title || String(u.emp_id)),

        }))

    },

    availableUserOptions() {

      return this.availableUsers.map(u => ({

        value: String(u.emp_id),

        label: u.label,

      }))

    },

    newMemberEmpIdsSummary() {

      if (!this.newMemberEmpIds.length) return 'Select employees…'

      const names = this.newMemberEmpIds.map(id => this.getAssigneeLabel(id, this.availableUserOptions))

      if (names.length <= 2) return names.join(', ')

      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`

    },

    responsibilityAssigneesSummary() {

      if (!this.newResponsibilityAssignees.length) return 'Select team members…'

      const names = this.newResponsibilityAssignees.map(id => this.getAssigneeLabel(id, this.responsibilityAssigneeOptions))

      if (names.length <= 2) return names.join(', ')

      return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`

    },

    wizardTeamRoster() {

      if (this.isEditMode) {

        const roster = this.visibleEditMembers.map(m => ({

          empId: String(m.empId),

          name: m.name,

          role: m.role,

        }))

        for (const m of this.pendingMembers) {

          roster.push({ empId: String(m.empId), name: m.name, role: m.role })

        }

        return roster

      }

      const roster = []

      if (this.currentEmpId != null) {

        roster.push({

          empId: String(this.currentEmpId),

          name: this.creatorDisplayName,

          role: PROJECT_ROLES.PROJECT_MANAGER,

        })

      }

      for (const m of this.pendingMembers) {

        roster.push({ empId: String(m.empId), name: m.name, role: m.role })

      }

      return roster

    },

    responsibilityAssigneeOptions() {

      return this.wizardTeamRoster

        .map(m => ({

          empId: String(m.empId),

          label: m.name || String(m.empId),

        }))

        .sort((a, b) => a.label.localeCompare(b.label))

    },

    canSubmitPendingResponsibility() {

      return isValidResponsibilityNote(this.newResponsibilityTitle)

        && this.newResponsibilityAssignees.length > 0

    },

    addMemberRoleOptions() {

      return this.roleOptions.filter(r => r.key !== PROJECT_ROLES.PROJECT_MANAGER)

    },

  },

  watch: {

    value(open) {

      if (open) this.resetForm()

    },

    project() {

      if (this.value) this.resetForm()

    },

  },

  methods: {

    avatarText,

    formatSize: formatMemberFileSize,

    resetForm() {

      this.step = 1

      this.editTab = 0

      this.editMembers = []

      this.removedMemberEmpIds = []

      this.originalMemberRoles = {}

      this.pendingMembers = []

      this.pendingResponsibilities = []

      this.resetMemberDraft()

      this.resetResponsibilityDraft()

      if (this.project) {

        this.form = {

          id: projectId(this.project),

          name: this.project.name || '',

          description: this.project.description || '',

          status: this.project.status || PROJECT_STATUSES.ACTIVE,

          color: this.project.color || PROJECT_COLOR_OPTIONS[0],

        }

        this.editMembers = normalizeMembers(this.project.members).map(m => ({ ...m }))

        this.originalMemberRoles = {}

        for (const m of this.editMembers) {

          this.originalMemberRoles[String(m.empId)] = m.role

        }

      } else {

        this.form = blankForm()

      }

    },

    resetMemberDraft() {

      this.newMemberEmpIds = []

      this.newMemberRole = PROJECT_ROLES.CONTRIBUTOR

    },

    resetResponsibilityDraft() {

      this.newResponsibilityTitle = ''

      this.newResponsibilityDescription = ''

      this.newResponsibilityAssignees = []

      this.newResponsibilityAttachments = []

      this.newResponsibilityFiles = null

      this.responsibilityFileErrors = []

      this.showResponsibilityAttachments = false

    },

    onHidden() {

      this.form = blankForm()

      this.step = 1

      this.editTab = 0

      this.editMembers = []

      this.removedMemberEmpIds = []

      this.originalMemberRoles = {}

      this.pendingMembers = []

      this.pendingResponsibilities = []

      this.resetMemberDraft()

      this.resetResponsibilityDraft()

    },

    goBack() {

      if (this.step > 1) this.step -= 1

    },

    async goToStep2() {

      const valid = await this.$refs.projectFormRules.validate()

      if (!valid) return

      this.step = 2

    },

    goToStep3() {

      if (this.newMemberEmpIds.length) this.addPendingMember()

      this.step = 3

    },

    getAssigneeLabel(empId, options) {

      const id = String(empId)

      const match = (options || []).find(option => String(option.empId ?? option.value) === id)

      if (match) return match.label

      const roster = this.isEditMode ? this.visibleEditMembers : this.wizardTeamRoster

      return roster.find(m => String(m.empId) === id)?.name || id

    },

    isProjectCreator(empId) {

      const creator = this.project?.createdBy ?? this.project?.created_by

      return creator != null && String(creator) === String(empId)

    },

    teamMemberRoleOptions(excludeEmpId = null) {

      const base = [

        { key: PROJECT_ROLES.PROJECT_MANAGER, label: 'Project Manager' },

        { key: PROJECT_ROLES.CONTRIBUTOR, label: 'Contributor' },

      ]

      const roster = this.isEditMode ? this.visibleEditMembers : this.wizardTeamRoster

      const hasOtherPm = roster.some(m => (

        m.role === PROJECT_ROLES.PROJECT_MANAGER

        && String(m.empId) !== String(excludeEmpId)

      ))

      if (hasOtherPm) {

        return base.filter(r => r.key === PROJECT_ROLES.CONTRIBUTOR)

      }

      return base

    },

    markMemberRemoved(empId) {

      const removedId = String(empId)

      if (this.isProjectCreator(removedId)) return

      if (!this.removedMemberEmpIds.includes(removedId)) {

        this.removedMemberEmpIds.push(removedId)

      }

      this.pendingResponsibilities = this.pendingResponsibilities

        .map(resp => ({

          ...resp,

          targetEmpIds: resp.targetEmpIds.filter(id => String(id) !== removedId),

        }))

        .filter(resp => resp.targetEmpIds.length > 0)

      this.newResponsibilityAssignees = this.newResponsibilityAssignees

        .filter(id => String(id) !== removedId)

    },

    getMembersToUpdate() {

      return this.visibleEditMembers

        .filter(m => {

          const orig = this.originalMemberRoles[String(m.empId)]

          return orig != null && orig !== m.role

        })

        .map(m => ({ ...m }))

    },

    addPendingMember() {

      if (!this.newMemberEmpIds.length) return

      for (const rawId of this.newMemberEmpIds) {

        const empId = String(rawId)

        if (this.pendingMembers.some(m => String(m.empId) === empId)) continue

        const user = this.availableUsers.find(u => String(u.emp_id) === empId)

          || (this.assignableUsers || []).find(

            u => String(u.emp_id ?? u.empId ?? u.id) === empId,

          )

        this.pendingMembers.push({

          empId,

          name: user?.label

            || (user?.first_name

              ? `${user.first_name} ${user.last_name || ''}`.trim()

              : (user?.name || user?.title || empId)),

          email: user?.email || null,

          role: this.newMemberRole,

        })

      }

      this.resetMemberDraft()

    },

    removePendingMember(idx) {

      const removed = this.pendingMembers[idx]

      this.pendingMembers.splice(idx, 1)

      if (removed) {

        const removedId = String(removed.empId)

        this.pendingResponsibilities = this.pendingResponsibilities

          .map(resp => ({

            ...resp,

            targetEmpIds: resp.targetEmpIds.filter(id => String(id) !== removedId),

          }))

          .filter(resp => resp.targetEmpIds.length > 0)

        this.newResponsibilityAssignees = this.newResponsibilityAssignees

          .filter(id => String(id) !== removedId)

      }

    },

    buildMemberDefaultsForEmpIds(empIds) {

      const defaults = {}

      const rosterById = new Map(this.wizardTeamRoster.map(m => [String(m.empId), m]))

      for (const rawId of empIds || []) {

        const empId = String(rawId)

        if (defaults[empId]) continue

        const member = rosterById.get(empId)

        if (member) {

          defaults[empId] = {

            name: member.name,

            role: member.role,

          }

          continue

        }

        const user = (this.assignableUsers || []).find(

          u => String(u.emp_id ?? u.empId ?? u.id) === empId,

        )

        if (!user) continue

        defaults[empId] = {

          name: user.first_name

            ? `${user.first_name} ${user.last_name || ''}`.trim()

            : (user.name || user.title || empId),

          email: user.email || null,

          role: PROJECT_ROLES.CONTRIBUTOR,

          addedBy: this.currentEmpId,

        }

      }

      return defaults

    },

    async appendFilesToPending(files) {

      this.processingFiles = true

      try {

        const { attachments, errors } = await filesToMemberAttachments(files, {

          uploadedBy: this.currentEmpId,

        })

        this.newResponsibilityAttachments = [...this.newResponsibilityAttachments, ...attachments]

        this.responsibilityFileErrors = errors

      } finally {

        this.processingFiles = false

      }

    },

    async onNewResponsibilityFilesSelected(files) {

      if (!files || (Array.isArray(files) && !files.length)) return

      await this.appendFilesToPending(files)

      this.newResponsibilityFiles = null

    },

    removeNewResponsibilityAttachment(idx) {

      this.newResponsibilityAttachments.splice(idx, 1)

    },

    addPendingResponsibility() {

      if (!this.canSubmitPendingResponsibility) return

      const targetEmpIds = [...this.newResponsibilityAssignees]

      this.pendingResponsibilities.push({

        targetEmpIds,

        memberDefaults: this.buildMemberDefaultsForEmpIds(targetEmpIds),

        responsibilityTitle: (this.newResponsibilityTitle || '').trim(),

        responsibilityDescription: (this.newResponsibilityDescription || '').trim(),

        attachments: [...this.newResponsibilityAttachments],

      })

      this.resetResponsibilityDraft()

    },

    removePendingResponsibility(idx) {

      this.pendingResponsibilities.splice(idx, 1)

    },

    async handleEditSave() {

      const valid = await this.$refs.projectFormRules.validate()

      if (!valid) {

        this.editTab = 0

        return

      }

      if (this.canManage) {

        if (this.newMemberEmpIds.length) this.addPendingMember()

        if (this.canSubmitPendingResponsibility) this.addPendingResponsibility()

      }

      this.$emit('submit', {

        ...this.form,

        mode: 'edit',

        membersToAdd: this.canManage ? [...this.pendingMembers] : [],

        membersToRemove: this.canManage ? [...this.removedMemberEmpIds] : [],

        membersToUpdate: this.canManage ? this.getMembersToUpdate() : [],

        responsibilitiesToAdd: this.canManage ? [...this.pendingResponsibilities] : [],

      })

    },

    handleCreateFinish({ skipTeam = false, skipResponsibilities = false } = {}) {

      if (!skipTeam && this.newMemberEmpIds.length) this.addPendingMember()

      if (!skipResponsibilities) {

        if (this.canSubmitPendingResponsibility) this.addPendingResponsibility()

      }

      this.$emit('submit', {

        ...this.form,

        mode: 'create',

        members: skipTeam ? [] : [...this.pendingMembers],

        responsibilities: skipResponsibilities ? [] : [...this.pendingResponsibilities],

      })

    },

  },

}

</script>



<style lang="scss" scoped>

.assignee-multiselect-toggle {

  width: 100%;

  font-weight: 400;

  text-align: left;

  text-decoration: none !important;



  &:hover,

  &:focus,

  &:active {

    text-decoration: none !important;

  }

}



.assignee-multiselect-menu {

  max-height: 220px;

  overflow-y: auto;

  padding: 0.5rem 0;

  min-width: 220px;

  .assignee-multiselect-item.custom-control {

    padding: 0.25rem 0.5rem 0.25rem 2.1rem;

    margin-bottom: 0;

    min-height: auto;

    width: 100%;

  }

  .assignee-multiselect-item .custom-control-label {

    cursor: pointer;

    line-height: 1.35;

  }

}



.project-selected-chips {

  gap: 0.25rem;

}



.project-color-swatch {

  width: 28px;

  height: 28px;

  border-radius: 50%;

  border: 2px solid transparent;

  cursor: pointer;

  padding: 0;

  transition: transform 0.15s ease, box-shadow 0.15s ease;



  &.active {

    border-color: #fff;

    box-shadow: 0 0 0 2px #7367f0;

    transform: scale(1.1);

  }

}



.project-wizard-steps {

  gap: 0;

}



.project-wizard-step {

  display: flex;

  align-items: center;

  gap: 6px;

  color: #b9b9c3;

  font-size: 0.875rem;



  &.active,

  &.done {

    color: #7367f0;

  }

}



.project-wizard-step-num {

  display: inline-flex;

  align-items: center;

  justify-content: center;

  width: 22px;

  height: 22px;

  border-radius: 50%;

  border: 1.5px solid currentColor;

  font-size: 0.8125rem;

  font-weight: 600;

}



.project-wizard-step.active .project-wizard-step-num,

.project-wizard-step.done .project-wizard-step-num {

  background: #7367f0;

  border-color: #7367f0;

  color: #fff;

}



.project-wizard-divider {

  flex: 1;

  height: 1px;

  background: rgba(34, 41, 47, 0.12);

  margin: 0 12px;

  min-width: 24px;

}



.project-wizard-step-label {

  white-space: nowrap;

}



.project-pending-members,

.project-pending-responsibilities {

  max-height: 160px;

  overflow-y: auto;

}



.project-pending-member,

.project-pending-responsibility {

  padding: 0.4rem 0;

  border-bottom: 1px solid rgba(34, 41, 47, 0.06);



  &:last-child {

    border-bottom: none;

  }

}



.project-member-attachment-row {

  padding: 0.25rem 0;

  gap: 0.25rem;

}



.add-responsibility-attach-toggle {

  font-size: 0.875rem;

}



.min-width-0 {

  min-width: 0;

}



.project-member-form-card {

  background: rgba(115, 103, 240, 0.03);

}



.project-settings-tabs ::v-deep .tab-content {

  display: none;

}

</style>
